import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X, Search } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "@/context/CartContext";
import { LOGO, SITE } from "@/lib/site";

export const Navbar = ({ overHero = false }) => {
    const { count, setOpen } = useCart();
    const [scrolled, setScrolled] = useState(false);
    const [menu, setMenu] = useState(false);
    const nav = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 60);
        window.addEventListener("scroll", onScroll);
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // transparent over hero until scrolled; otherwise solid cream
    const solid = scrolled || !overHero;
    const links = [
        { label: "Tienda", to: "/tienda" },
        { label: "Catálogo", to: "/catalogo" },
        { label: "Paquetes", to: "/combos", estatico: true },
        { label: "Diseño", to: "/diseno", estatico: true, destacado: true },
        { label: "Ambientes", to: "/diseno#ambientes", estatico: true },
        { label: "Calculadora", to: "/calculadora" },
        { label: "Presupuesto", to: "/#presupuesto" },
        { label: "Mayoreo", to: "/#mayoreo" },
    ];

    const textCol = solid ? "text-ink" : "text-white";

    return (
        <>
            <header
                data-testid="navbar"
                className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
                    solid ? "bg-crema/90 backdrop-blur-xl border-b border-linea shadow-soft" : "bg-transparent"
                }`}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 md:h-20 flex items-center justify-between">
                    <Link to="/" data-testid="logo-link" className="flex items-center gap-3 shrink-0">
                        <img
                            src={solid ? LOGO.color : LOGO.white}
                            alt={SITE.name}
                            className="h-11 md:h-14 w-auto object-contain"
                        />
                    </Link>

                    <nav className="hidden lg:flex items-center gap-6">
                        {links.map((l) => {
                            const clase = `text-sm font-medium transition-colors ${
                                l.destacado ? "text-naranja font-semibold hover:brightness-90" : `${textCol} hover:text-naranja`
                            }`;
                            // las páginas estáticas no viven en el router: van por el navegador
                            return l.estatico ? (
                                <a key={l.label} href={l.to} data-testid={`nav-${l.label.toLowerCase()}`} className={clase}>
                                    {l.label}
                                </a>
                            ) : (
                                <Link key={l.label} to={l.to} data-testid={`nav-${l.label.toLowerCase()}`} className={clase}>
                                    {l.label}
                                </Link>
                            );
                        })}
                    </nav>

                    <div className="flex items-center gap-2 md:gap-4">
                        <button
                            data-testid="nav-search-btn"
                            onClick={() => nav("/tienda")}
                            className={`${textCol} hover:text-naranja transition-colors p-1`}
                            aria-label="Buscar"
                        >
                            <Search size={20} strokeWidth={1.8} />
                        </button>
                        <button
                            data-testid="cart-open-btn"
                            onClick={() => setOpen(true)}
                            className={`relative ${textCol} hover:text-naranja transition-colors p-1`}
                            aria-label="Carrito"
                        >
                            <ShoppingBag size={22} strokeWidth={1.8} />
                            {count > 0 && (
                                <span
                                    data-testid="cart-count-badge"
                                    className="absolute -top-1.5 -right-1.5 bg-naranja text-white text-[10px] font-bold h-5 min-w-5 px-1 rounded-full flex items-center justify-center"
                                >
                                    {count}
                                </span>
                            )}
                        </button>
                        <button
                            data-testid="mobile-menu-btn"
                            onClick={() => setMenu(true)}
                            className={`lg:hidden ${textCol} p-1`}
                            aria-label="Menú"
                        >
                            <Menu size={24} />
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {menu && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[60] bg-crema lg:hidden"
                        data-testid="mobile-menu"
                    >
                        <div className="flex justify-between items-center h-16 px-5 border-b border-linea">
                            <img src={LOGO.color} alt="" className="h-11" />
                            <button onClick={() => setMenu(false)} data-testid="mobile-menu-close" className="text-ink">
                                <X size={26} />
                            </button>
                        </div>
                        <div className="flex flex-col p-6 gap-1">
                            {links.map((l, i) => (
                                <motion.div key={l.label} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }}>
                                    {l.estatico ? (
                                        <a
                                            href={l.to}
                                            onClick={() => setMenu(false)}
                                            className={`block font-display font-bold text-3xl py-3 border-b border-linea/70 transition-colors ${
                                                l.destacado ? "text-naranja" : "text-ink hover:text-naranja"
                                            }`}
                                        >
                                            {l.label}
                                        </a>
                                    ) : (
                                        <Link
                                            to={l.to}
                                            onClick={() => setMenu(false)}
                                            className="block font-display font-bold text-3xl text-ink py-3 border-b border-linea/70 hover:text-naranja transition-colors"
                                        >
                                            {l.label}
                                        </Link>
                                    )}
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
